
import {
  computed, defineComponent, onBeforeUnmount, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import Tooltip from 'primevue/tooltip';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import StepShell from '@/components/onboarding-v2/shared/StepShell.vue';
import { getEcommerceModule, getEcommerceModulesList, checkIfShopConnected } from '@/configs/complete';
import { updateShop } from '@/composables/shop/Shops';
import { nestCall, NestApiType } from '@/composables/nestApi';
import { Me, UserState } from '@/composables/User';
import { showToastError, showToastSuccess } from '@/helpers';

const POLL_INTERVAL_MS = 5000;
const ENABLED_PLATFORM_IDS = new Set(['prestashop', 'magento', 'magento2', 'woocommerce']);

export default defineComponent({
  name: 'ConnectStep',

  components: {
    StepShell,
    SpmButton,
    ConfirmDialog,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    canGoBack: { type: Boolean, default: false },
    /**
     * When true, displays a prominent warning banner at the top of the
     * step explaining that the user MUST uninstall the legacy V4
     * Shopimind module before installing the V5 one. Used by the V4 → V5
     * migration flow (`MigrationV5.vue`). Kept off by default so the
     * regular onboarding for fresh shops doesn't show a confusing
     * "uninstall first" instruction (there's nothing to uninstall).
     */
    showMigrationWarning: { type: Boolean, default: false },
  },

  emits: ['next', 'back'],

  setup(_, { emit }) {
    const { t } = useI18n();
    const confirm = useConfirm();
    const instructionsOpen = ref(false);

    const platform = computed(() => UserState.activeShop?.solutionEcommerce ?? '');
    const isShopify = computed(() => platform.value === 'shopify');
    const platformMeta = computed(() => (platform.value
      ? getEcommerceModule(platform.value)
      : null));

    const instructions = computed<string[]>(() => {
      const meta: any = platformMeta.value;
      if (!meta) return [];
      return [
        ...(meta.downloadInstructions ?? []),
        ...(meta.installationInstructions ?? []),
      ];
    });

    const iconBroken = ref(false);
    const platformIconSrc = computed(() => {
      if (iconBroken.value || !platform.value) return '';
      return `/images/ecommerce-solutions/${platform.value}.svg`;
    });

    const shopIdentifier = ref('');
    const apiKeyValue = ref('');
    const keysLoading = ref(false);
    const regenerating = ref(false);
    const keyIsRevealed = ref(false);
    const currentKey = ref<{ id: number; prefix: string } | null>(null);

    const apiKeys = computed(() => [
      {
        id: 'identifiant',
        label: t('completeV2.steps.connect.identifiantLabel'),
        value: shopIdentifier.value,
      },
      {
        id: 'apiKey',
        label: t('completeV2.steps.connect.apiKeyLabel'),
        value: apiKeyValue.value,
      },
    ]);

    const createKey = async () => {
      const shopId = UserState.activeShop?.id;
      if (!shopId) return;
      const created: any = await nestCall(
        'post',
        NestApiType.DEFAULT,
        `/shop/keys/${shopId}`,
        {},
        '',
        { key_name: 'Module' },
      );
      if (created?.success) {
        currentKey.value = {
          id: created.data?.id,
          prefix: created.data?.prefix ?? '',
        };
        apiKeyValue.value = created.data?.full_key ?? '';
        keyIsRevealed.value = true;
      }
    };

    const loadApiKeys = async () => {
      const shopId = UserState.activeShop?.id;
      if (!shopId) return;
      keysLoading.value = true;
      try {
        const existing: any = await nestCall('get', NestApiType.DEFAULT, `/shop/keys/${shopId}`);
        if (existing?.success) {
          shopIdentifier.value = existing.data?.identifiant ?? '';
          const keys: Array<{ id: number; prefix: string }> = (existing.data?.keys ?? [])
            .filter((k: { prefix: string }) => !k.prefix?.toLowerCase().startsWith('sys'));
          if (keys.length > 0) {
            currentKey.value = { id: keys[0].id, prefix: keys[0].prefix };
            apiKeyValue.value = `${keys[0].prefix}********************`;
            keyIsRevealed.value = false;
            return;
          }
        }
        await createKey();
      } catch {
        await showToastError('GENERIC_ERROR');
      } finally {
        keysLoading.value = false;
      }
    };

    const performRegenerate = async () => {
      const shopId = UserState.activeShop?.id;
      if (!shopId || !currentKey.value?.id) return;
      regenerating.value = true;
      try {
        await nestCall('delete', NestApiType.DEFAULT, `/shop/keys/${shopId}/${currentKey.value.id}`);
        await createKey();
        await showToastSuccess(t('completeV2.steps.connect.regenerated'));
      } catch {
        await showToastError('GENERIC_ERROR');
      } finally {
        regenerating.value = false;
      }
    };

    const regenerateKey = () => {
      if (!currentKey.value?.id) return;
      confirm.require({
        message: t('completeV2.steps.connect.regenerateConfirm'),
        header: t('completeV2.steps.connect.regenerateConfirmTitle'),
        icon: 'far fa-triangle-exclamation',
        acceptLabel: t('completeV2.steps.connect.regenerateAccept'),
        rejectLabel: t('completeV2.backCta'),
        acceptClass: 'p-button-danger',
        accept: () => {
          performRegenerate();
        },
      });
    };

    const editorOpen = ref(false);
    const draftPlatform = ref('');
    const savingPlatform = ref(false);

    const availablePlatforms = computed(() => getEcommerceModulesList().map((p) => ({
      ...p,
      disabled: p.disabled || !ENABLED_PLATFORM_IDS.has(p.id),
    })));

    const canDownloadModule = computed(() => Boolean(
      platform.value
      && ENABLED_PLATFORM_IDS.has(platform.value)
      && platformMeta.value?.downloadLink,
    ));

    const cancelEditor = () => {
      editorOpen.value = false;
      draftPlatform.value = platform.value;
    };

    const confirmPlatform = async () => {
      if (!draftPlatform.value || draftPlatform.value === platform.value) return;
      if (!UserState.activeShop?.id) return;
      savingPlatform.value = true;
      try {
        await updateShop([{
          id_shop: UserState.activeShop.id,
          solution_ecommerce: draftPlatform.value,
          date_modification: new Date().toISOString(),
        }]);
        await Me(true, UserState.activeShop.id);
        editorOpen.value = false;
      } catch {
        await showToastError('GENERIC_ERROR');
      } finally {
        savingPlatform.value = false;
      }
    };

    const connected = ref(false);
    const shopifyConnecting = ref(false);
    const shopifyError = ref<string | null>(null);
    let pollHandle: number | null = null;

    const connectShopify = async () => {
      if (shopifyConnecting.value) return;
      shopifyConnecting.value = true;
      shopifyError.value = null;
      try {
        const res: any = await nestCall('post', NestApiType.AUTH, '/shopify/connect');
        if (!res?.success) {
          shopifyError.value = res?.message || t('completeV2.steps.connect.shopifyConnectError');
        }
      } catch (e: any) {
        shopifyError.value = e?.message || t('completeV2.steps.connect.shopifyConnectError');
      } finally {
        shopifyConnecting.value = false;
      }
    };

    const copiedKeyId = ref<string | null>(null);
    let copyResetHandle: number | null = null;

    const copy = async (value: string, keyId?: string) => {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        await showToastSuccess(t('completeV2.steps.connect.copied'));
        if (keyId) {
          copiedKeyId.value = keyId;
          if (copyResetHandle !== null) window.clearTimeout(copyResetHandle);
          copyResetHandle = window.setTimeout(() => {
            copiedKeyId.value = null;
            copyResetHandle = null;
          }, 1800);
        }
      } catch {
        await showToastError('GENERIC_ERROR');
      }
    };

    const poll = async () => {
      try {
        if (await checkIfShopConnected()) {
          connected.value = true;
          if (pollHandle !== null) {
            window.clearInterval(pollHandle);
            pollHandle = null;
          }
          emit('next');
        }
      } catch {
        /* noop */
      }
    };

    onMounted(async () => {
      draftPlatform.value = platform.value;
      if (await checkIfShopConnected()) {
        connected.value = true;
        emit('next');
        return;
      }

      if (platform.value && platform.value !== 'shopify' && UserState.activeShop?.id) {
        await loadApiKeys();
      }

      if (isShopify.value && UserState.activeShop?.id) {
        await connectShopify();
      }

      pollHandle = window.setInterval(poll, POLL_INTERVAL_MS);
    });

    onBeforeUnmount(() => {
      if (pollHandle !== null) {
        window.clearInterval(pollHandle);
        pollHandle = null;
      }
    });

    return {
      t,
      platform,
      platformMeta,
      instructions,
      instructionsOpen,
      platformIconSrc,
      iconBroken,
      isShopify,
      apiKeys,
      keysLoading,
      keyIsRevealed,
      regenerating,
      regenerateKey,
      connected,
      copy,
      copiedKeyId,
      editorOpen,
      draftPlatform,
      savingPlatform,
      availablePlatforms,
      canDownloadModule,
      cancelEditor,
      confirmPlatform,
      shopifyConnecting,
      shopifyError,
      connectShopify,
    };
  },
});
